import { ReactNode, FC, createContext, useContext, useEffect, useMemo } from 'react';

import { useExperimentsGetExperimentByFeatureId } from '@eneco/dc/hooks';
import useDC from '@eneco/dc/useDC';
import {
  DEVICE_ID_COOKIE_AGE,
  GrowthBook,
  STICKY_BUCKET_COOKIE_AGE,
  getGrowthbookTrackingEvent,
  getStickyBucketAssignmentDocs,
} from '@eneco/growthbook';
import { useRouter } from '@eneco/next';
import { useGTM } from '@eneco/tracking';
import { Attributes, BrowserCookieStickyBucketService, GrowthBookProvider } from '@growthbook/growthbook-react';
import Cookies from 'js-cookie';

interface ExperimentProviderProps {
  featureId?: string;
  children: ReactNode;
  attributes?: Attributes;
}

const ExperimentLoadingContext = createContext<boolean>(false);

export const ExperimentLoadingProvider: FC<{ isLoading: boolean; children: ReactNode }> = ({ isLoading, children }) => {
  return <ExperimentLoadingContext.Provider value={isLoading}>{children}</ExperimentLoadingContext.Provider>;
};

export const useExperimentLoading = () => {
  return useContext(ExperimentLoadingContext);
};

export const ExperimentProvider = ({ children, featureId, attributes }: ExperimentProviderProps) => {
  const router = useRouter();
  const dc = useDC();
  const { pushToDataLayer } = useGTM();
  const { data, isLoading } = useExperimentsGetExperimentByFeatureId(featureId ? { featureId } : null, {
    loadingTimeout: 1000,
  });

  const growthbookInstance = useMemo(() => {
    // NOTE: holdout is intentionally NOT applied here. This provider fetches a single feature by id
    // (getExperimentByFeatureId), so the holdout prerequisite feature is not part of the payload.
    // Injecting a holdout `parentConditions` gate would fail closed and disable the experiment.
    // Holdout gating happens on the list-based paths (SSR getFeatureVariants, energyFlow) where the
    // holdout feature is present. See applyHoldoutGate in @eneco/growthbook.
    const features = data?.definition && featureId ? { [featureId]: JSON.parse(data.definition) } : {};
    const path = router?.activePath;
    return new GrowthBook({
      attributes: { path, ...dc, ...attributes },
      getDeviceId: cookieName => Cookies.get(cookieName),
      stickyBucketAssignmentDocs: getStickyBucketAssignmentDocs(Cookies.get()),
      stickyBucketService: new BrowserCookieStickyBucketService({
        jsCookie: Cookies,
        cookieAttributes: { expires: STICKY_BUCKET_COOKIE_AGE },
      }),
      persistDeviceId: (cookieName, deviceId) => Cookies.set(cookieName, deviceId, { expires: DEVICE_ID_COOKIE_AGE }),
      trackingCallback: (experiment, result, deviceId) => {
        pushToDataLayer(getGrowthbookTrackingEvent(experiment, result, deviceId));
      },
    }).initSync({
      payload: { features },
    });
  }, [data, attributes, featureId, router?.activePath, dc]);

  useEffect(() => {
    const updateGrowthBookURL = () => {
      growthbookInstance.setURL(window.location.href);
    };
    router.events.on('routeChangeComplete', updateGrowthBookURL);
    return () => router.events.off('routeChangeComplete', updateGrowthBookURL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ExperimentLoadingProvider isLoading={isLoading}>
      <GrowthBookProvider growthbook={growthbookInstance}>{children}</GrowthBookProvider>
    </ExperimentLoadingProvider>
  );
};
